import CloseIcon from "@mui/icons-material/Close";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Snackbar,
    TextField,
    Typography,
} from "@mui/material";
import { useState } from "react";
import medineerHelpSupportNotificationService from "../services/medineerHelpSupportNotificationService";
const HelpSupportDialog = ({ open, onClose }) => {
    const [formData, setFormData] = useState({
        subject: "",
        email: "",
        mobile: "",
        description: "",
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);


    const [snackbar, setSnackbar] = useState({
        open: false,
        severity: "success",
        message: "",
    });

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrors((prev) => ({
            ...prev,
            [name]: "",
        }));
    };

    const validate = () => {
        let temp = {};

        if (!formData.subject.trim())
            temp.subject = "Subject is required.";

        if (!formData.email.trim()) {
            temp.email = "Email is required.";
        } else if (
            !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
        ) {
            temp.email = "Enter a valid email.";
        }

        if (!formData.mobile.trim()) {
            temp.mobile = "Mobile number is required.";
        } else if (!/^[0-9]{10}$/.test(formData.mobile)) {
            temp.mobile = "Enter a valid 10-digit mobile number.";
        }

        if (!formData.description.trim()) {
            temp.description = "Description is required.";
        }

        setErrors(temp);

        return Object.keys(temp).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        try {
            setLoading(true);

            const payload = {
                email: formData.email,
                mobile: formData.mobile,
                subject: formData.subject,
                description: formData.description,
            };

            await medineerHelpSupportNotificationService(payload);

            setSnackbar({
                open: true,
                severity: "success",
                message:
                    "Your request has been submitted successfully. Our support team will contact you shortly.",
            });

            setFormData({
                subject: "",
                email: "",
                mobile: "",
                description: "",
            });

            onClose();
        } catch (error) {
            console.error(error);

            setSnackbar({
                open: true,
                severity: "error",
                message:
                    error?.response?.data?.message ||
                    "Something went wrong while submitting your request.",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={loading ? undefined : onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Box display="flex" alignItems="center" gap={1}>
                            <SupportAgentOutlinedIcon
                                color="primary"
                                fontSize="large"
                            />

                            <Typography
                                fontWeight={700}
                                fontSize="2rem"
                                color="#00264D"
                            >
                                Help & Support
                            </Typography>
                        </Box>

                        <IconButton
                            disabled={loading}
                            onClick={onClose}
                        >
                            <CloseIcon sx={{ fontSize: 23 }} />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ pt: 3 }}>
                    <Typography
                        color="text.secondary"
                        sx={{
                            mb: 3,
                            fontSize: "1.45rem",
                            lineHeight: 1.8,
                        }}
                    >
                        Need assistance? Facing payment issues, subscription
                        problems, technical errors, or have a general query?
                        Fill out the details below and our support team will respond as soon as possible.
                    </Typography>

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        error={!!errors.subject}
                        helperText={errors.subject}
                        sx={{
                            "& .MuiInputLabel-root": {
                                fontSize: "1.5rem",
                            },
                            "& .MuiInputBase-input": {
                                fontSize: "1.5rem",
                            },
                            "& .MuiFormHelperText-root": {
                                fontSize: "1.25rem",
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        sx={{
                            "& .MuiInputLabel-root": {
                                fontSize: "1.5rem",
                            },
                            "& .MuiInputBase-input": {
                                fontSize: "1.5rem",
                            },
                            "& .MuiFormHelperText-root": {
                                fontSize: "1.25rem",
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Mobile Number"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        error={!!errors.mobile}
                        helperText={errors.mobile}
                        sx={{
                            "& .MuiInputLabel-root": {
                                fontSize: "1.5rem",
                            },
                            "& .MuiInputBase-input": {
                                fontSize: "1.5rem",
                            },
                            "& .MuiFormHelperText-root": {
                                fontSize: "1.25rem",
                            },
                        }}
                        inputProps={{
                            maxLength: 10,
                            inputMode: "numeric",
                        }}
                    />

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Describe your issue"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        error={!!errors.description}
                        helperText={errors.description}
                        sx={{
                            "& .MuiInputLabel-root": {
                                fontSize: "1.5rem",
                            },
                            "& .MuiInputBase-input": {
                                fontSize: "1.5rem",
                                lineHeight: 1.8,
                            },
                            "& .MuiFormHelperText-root": {
                                fontSize: "1.25rem",
                            },
                        }}
                        multiline
                        rows={5}
                    />
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        pb: 3,
                    }}
                >
                    <Button
                        disabled={loading}
                        onClick={onClose}
                        sx={{
                            textTransform: "capitalize",
                            fontSize: "1.45rem",
                            fontWeight: 600,
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading}
                        sx={{
                            minWidth: 180,
                            textTransform: "capitalize",
                            borderRadius: "10px",
                            fontSize: "1.45rem",
                            fontWeight: 600,
                        }}
                    >
                        {loading ? (
                            <>
                                <CircularProgress
                                    size={22}
                                    color="inherit"
                                    sx={{ mr: 1 }}
                                />
                                Sending...
                            </>
                        ) : (
                            "Send Request"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() =>
                    setSnackbar((prev) => ({
                        ...prev,
                        open: false,
                    }))
                }
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snackbar.severity}
                    sx={{
                        width: "100%",
                        fontSize: "15px",
                        fontFamily: "Poppins",
                    }}
                    onClose={() =>
                        setSnackbar((prev) => ({
                            ...prev,
                            open: false,
                        }))
                    }
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default HelpSupportDialog;